import {
  Box,
  Flex,
  Icon,
  Input,
  Kbd,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import {
  Briefcase01Icon,
  CalendarIcon,
  DataflowIcon4,
  File04Icon,
  Grid01Icon,
  HomeIcon,
  ListIcon,
  MessageTextSquare01Icon,
  Monitor02Icon,
  SearchLgIcon,
  Settings01Icon,
  SlashIcon,
  Speedometer01Icon,
  UserPlus01Icon,
  Users02Icon,
} from '../assets/icons'
import { getMyPermissions } from '../api/settings/pagePermissions'
import FastAvatar from './image/FastAvatar'
import {
  searchEmployees,
  searchCandidates,
  searchDocuments,
  searchContent,
  searchJobProfiles,
  type SearchEmployeeResult,
  type SearchCandidateResult,
  type SearchDocumentResult,
  type SearchContentResult,
  type SearchJobProfileResult,
} from '../api/search/search'
import { useRole } from '../hooks/auth/useRole'
import { myPermissionsQueryOptions } from '../lib/performance/queryOptions'
import { expandPlanningTabs, resolvePlanningPath } from '../lib/navigation/navigationConfig'

interface SearchResult {
  id: string
  type: 'page' | 'employee' | 'candidate' | 'document' | 'content' | 'job_profile'
  title: string
  subtitle?: string
  href: string
  icon?: React.ElementType
  avatar?: string
}

interface PageItem {
  icon: React.ElementType
  title: string
  path: string
  keywords: string[]
}

// Helper to get pages based on role - matches Navbar.tsx structure
const getFirstPlanningPath = (tabs: string[]): string => resolvePlanningPath(tabs)

const getPagesByRole = (
  role: string | null,
  isAdmin: boolean,
  planningTabs: string[],
  supportTabs: string[],
  t: TFunction,
): PageItem[] => {
  const pages: PageItem[] = []

  if (!isAdmin) {
    // Regular user pages
    pages.push(
      { icon: HomeIcon, title: t('navigation.home'), path: '/user/home', keywords: ['home', 'dashboard'] },
      { icon: Users02Icon, title: t('navigation.myProfile'), path: '/user/profile', keywords: ['profile', 'my profile', 'personal'] },
      { icon: Grid01Icon, title: t('navigation.organization'), path: '/user/organization', keywords: ['organization', 'teams', 'structure'] },
      { icon: File04Icon, title: t('navigation.documents'), path: '/user/document/documents', keywords: ['documents', 'files', 'hr info'] }
    )
    if (planningTabs.length > 0) {
      pages.push({ icon: CalendarIcon, title: t('navigation.planning'), path: getFirstPlanningPath(planningTabs), keywords: ['planning', 'calendar', 'attendance', 'schedule', 'leave'] })
    }
  } else if (role === 'org_admin' || role === 'super_admin') {
    // Super admin pages - full access
    pages.push(
      { icon: HomeIcon, title: t('navigation.home'), path: '/user/home', keywords: ['home', 'dashboard'] },
      { icon: ListIcon, title: t('navigation.todo'), path: '/user/todo', keywords: ['todo', 'tasks', 'list'] },
      { icon: DataflowIcon4, title: t('navigation.workforce'), path: '/user/workforce/people', keywords: ['workforce', 'people', 'employees', 'staff', 'hr'] },
      { icon: Speedometer01Icon, title: t('navigation.evaluations'), path: '/user/evaluations', keywords: ['evaluations', 'reviews', 'performance'] },
      { icon: UserPlus01Icon, title: t('navigation.recruitment'), path: '/user/recruitment/candidates', keywords: ['recruitment', 'hiring', 'candidates'] },
      { icon: DataflowIcon4, title: t('navigation.outflow'), path: '/user/outflow/exit-management', keywords: ['outflow', 'exits', 'turnover'] },
      { icon: Speedometer01Icon, title: t('navigation.analytics'), path: '/user/analytics', keywords: ['analytics', 'reports', 'insights'] },
      { icon: CalendarIcon, title: t('navigation.planning'), path: getFirstPlanningPath(planningTabs), keywords: ['planning', 'calendar', 'attendance', 'schedule', 'leave'] },
      { icon: Users02Icon, title: t('navigation.payroll'), path: '/user/payroll/people', keywords: ['payroll', 'salary', 'compensation'] },
      { icon: File04Icon, title: t('navigation.documents'), path: '/user/admin/document/documents', keywords: ['documents', 'files', 'templates'] },
      { icon: Briefcase01Icon, title: t('navigation.classifications'), path: '/user/classification/jobprofile', keywords: ['classifications', 'job profile', 'skills'] },
      { icon: Grid01Icon, title: t('navigation.explore'), path: '/user/explore/content', keywords: ['explore', 'discover', 'browse', 'library', 'content', 'create'] }
    )
  } else if (role === 'country_admin') {
    // Admin pages - NO classifications, NO org settings
    pages.push(
      { icon: HomeIcon, title: t('navigation.home'), path: '/user/home', keywords: ['home', 'dashboard'] },
      { icon: DataflowIcon4, title: t('navigation.workforce'), path: '/user/workforce/people', keywords: ['workforce', 'people', 'employees', 'staff', 'hr'] },
      { icon: Speedometer01Icon, title: t('navigation.evaluations'), path: '/user/evaluations', keywords: ['evaluations', 'reviews', 'performance'] },
      { icon: Speedometer01Icon, title: t('navigation.analytics'), path: '/user/analytics', keywords: ['analytics', 'reports', 'insights'] }
    )
    if (planningTabs.length > 0) {
      pages.push({ icon: CalendarIcon, title: t('navigation.planning'), path: getFirstPlanningPath(planningTabs), keywords: ['planning', 'calendar', 'attendance', 'schedule', 'leave'] })
    }
    pages.push(
      { icon: Users02Icon, title: t('navigation.payroll'), path: '/user/payroll/people', keywords: ['payroll', 'salary', 'compensation'] },
      { icon: File04Icon, title: t('navigation.documents'), path: '/user/admin/document/documents', keywords: ['documents', 'files', 'templates'] }
    )
  } else if (role === 'finance') {
    // Finance pages - NO explore, NO content, NO paths, NO evaluations, NO classifications
    pages.push(
      { icon: HomeIcon, title: t('navigation.home'), path: '/user/home', keywords: ['home', 'dashboard'] },
      { icon: DataflowIcon4, title: t('navigation.workforce'), path: '/user/workforce/people', keywords: ['workforce', 'people', 'employees', 'staff', 'hr'] },
      )
    if (planningTabs.length > 0) {
      pages.push({ icon: CalendarIcon, title: t('navigation.planning'), path: getFirstPlanningPath(planningTabs), keywords: ['planning', 'calendar', 'attendance', 'schedule', 'leave'] })
    }
    pages.push(
      { icon: Users02Icon, title: t('navigation.payroll'), path: '/user/payroll/people', keywords: ['payroll', 'salary', 'compensation'] },
      { icon: File04Icon, title: t('navigation.documents'), path: '/user/admin/document/documents', keywords: ['documents', 'files', 'templates'] }
    )
  } else if (role === 'team_leader') {
    // Team leader pages - NO content, NO analytics, NO payroll, NO classifications, NO org settings
    pages.push(
      { icon: HomeIcon, title: t('navigation.home'), path: '/user/home', keywords: ['home', 'dashboard'] },
      { icon: DataflowIcon4, title: t('navigation.workforce'), path: '/user/workforce/people', keywords: ['workforce', 'people', 'employees', 'staff', 'hr'] },
    )
    if (planningTabs.length > 0) {
      pages.push({ icon: CalendarIcon, title: t('navigation.planning'), path: getFirstPlanningPath(planningTabs), keywords: ['planning', 'calendar', 'attendance', 'schedule', 'leave'] })
    }
    pages.push({ icon: File04Icon, title: t('navigation.documents'), path: '/user/admin/document/documents', keywords: ['documents', 'files', 'templates'] })
  }

  // Add common bottom items (FAQ) to all roles
  pages.push(
    { icon: MessageTextSquare01Icon, title: t('navigation.aiAssistant'), path: '/user/chat', keywords: ['ai', 'assistant', 'chat', 'help'] },
    { icon: HomeIcon, title: t('navigation.faq'), path: '/user/faq', keywords: ['faq', 'questions', 'help', 'support', 'frequently asked'] }
  )

  if (role === 'org_admin' || role === 'super_admin' || supportTabs.includes('hr-chat')) {
    pages.push({ icon: MessageTextSquare01Icon, title: t('navigation.hrChat', 'HR Chat'), path: '/user/hr-chat', keywords: ['hr', 'chat', 'human resources', 'support'] })
  }
  
  
  // Settings only for org_admin and finance roles
  if (role === 'org_admin' || role === 'super_admin') {
    pages.push({ icon: Settings01Icon, title: t('navigation.settings'), path: '/user/settings/general', keywords: ['settings', 'preferences', 'configuration'] })
  }

  return pages
}

const getTabsByRole = (
  role: string | null,
  isAdmin: boolean,
  planningTabs: string[],
  myPermissions: any,
  t: TFunction,
): PageItem[] => {
  const tabs: PageItem[] = []

  const filterPlanningTabs = (all: PageItem[]) => {
    if (role === 'org_admin' || role === 'super_admin') return all
    if (!planningTabs.length) return []
    const effective = expandPlanningTabs(planningTabs)
    return all.filter((item) => effective.has(item.path.split('/').pop()!))
  }

  const filterRecruitmentTabs = (all: PageItem[]) => {
    if (role === 'org_admin' || role === 'super_admin') return all
    if (!isAdmin) return []
    const allowed = new Set(myPermissions?.recruitment || [])
    return all.filter((item) => allowed.has(item.path.split('/').pop()!))
  }

  if (!isAdmin) {
    tabs.push(
      { icon: File04Icon, title: t('documents.tabs.myDocuments'), path: '/user/document/documents', keywords: ['documents', 'my documents'] },
      { icon: File04Icon, title: t('documents.tabs.hrDocuments'), path: '/user/document/hrinfo', keywords: ['hr documents', 'hr info'] },
      { icon: Grid01Icon, title: t('organization.tabs.myTeams'), path: '/user/organization', keywords: ['my teams', 'organization'] },
      { icon: Grid01Icon, title: t('organization.tabs.myOrganization'), path: '/user/organization/organization', keywords: ['my organization', 'structure'] },
    )
    if ((myPermissions?.explore?.length ?? 0) > 0) {
      tabs.push(
        { icon: Grid01Icon, title: t('explore.tabs.explore'), path: '/user/explore/content', keywords: ['explore', 'content'] },
        { icon: Grid01Icon, title: t('explore.tabs.paths'), path: '/user/explore/userpath', keywords: ['paths', 'learning paths'] },
      )
    }
    tabs.push(...filterPlanningTabs([
      { icon: CalendarIcon, title: t('planning.tabs.calendar'), path: '/user/planning/attendance-calendar', keywords: ['attendance calendar'] },
      { icon: CalendarIcon, title: t('planning.tabs.pastRequests'), path: '/user/planning/past-requests', keywords: ['past requests'] },
      { icon: CalendarIcon, title: t('planning.tabs.attendance'), path: '/user/planning/attendance', keywords: ['attendance'] },
      { icon: CalendarIcon, title: t('planning.tabs.leaveManagement'), path: '/user/planning/leave-management', keywords: ['leave management'] },
      { icon: CalendarIcon, title: t('planning.tabs.workspace'), path: '/user/planning/workspace', keywords: ['workspace'] },
      { icon: CalendarIcon, title: t('planning.tabs.timeTracking'), path: '/user/planning/time-tracking', keywords: ['time tracking'] },
    ]))
  } else {
    tabs.push(
      { icon: DataflowIcon4, title: t('workforce.tabs.teams'), path: '/user/workforce/teams', keywords: ['teams'] },
      { icon: DataflowIcon4, title: t('workforce.tabs.organization'), path: '/user/workforce/organization', keywords: ['organization'] },
      { icon: DataflowIcon4, title: t('workforce.tabs.assets'), path: '/user/workforce/assets', keywords: ['assets'] },
    )
    if (role === 'org_admin' || role === 'super_admin' || role === 'country_admin' || role === 'finance') {
      tabs.push(
        { icon: DataflowIcon4, title: t('workforce.tabs.mutations'), path: '/user/workforce/mutations', keywords: ['mutations'] },
      )
    }

    tabs.push(
      { icon: Speedometer01Icon, title: t('evaluations.tabs.questions', 'Questions'), path: '/user/evaluations/questions', keywords: ['evaluation questions'] },
    )

    tabs.push(...filterRecruitmentTabs([
      { icon: UserPlus01Icon, title: t('recruitment.tabs.pipeline'), path: '/user/recruitment/pipeline', keywords: ['pipeline'] },
      { icon: UserPlus01Icon, title: t('recruitment.tabs.interviews'), path: '/user/recruitment/interviews', keywords: ['interviews'] },
      { icon: UserPlus01Icon, title: t('recruitment.tabs.vacancy'), path: '/user/recruitment/vacancy', keywords: ['vacancy'] },
      { icon: UserPlus01Icon, title: t('recruitment.tabs.overview'), path: '/user/recruitment/overview', keywords: ['overview'] },
      { icon: UserPlus01Icon, title: t('recruitment.tabs.inflow'), path: '/user/recruitment/inflow', keywords: ['inflow'] },
      { icon: UserPlus01Icon, title: t('recruitment.tabs.reports'), path: '/user/recruitment/reports', keywords: ['reports'] },
    ]))

    if (role === 'org_admin' || role === 'super_admin') {
      tabs.push(
        { icon: DataflowIcon4, title: t('outflow.tabs.exitInterviews'), path: '/user/outflow/exit-interviews', keywords: ['exit interviews'] },
        { icon: DataflowIcon4, title: t('outflow.tabs.outstandingAssets'), path: '/user/outflow/outstanding-assets', keywords: ['outstanding assets'] },
        { icon: DataflowIcon4, title: t('outflow.tabs.turnoverReports'), path: '/user/outflow/turnover-reports', keywords: ['turnover reports'] },
      )
    }

    tabs.push(
      { icon: Briefcase01Icon, title: t('classification.tabs.skills'), path: '/user/classification/skills', keywords: ['skills'] },
      { icon: Briefcase01Icon, title: t('classification.tabs.roles'), path: '/user/classification/roles', keywords: ['roles'] },
      { icon: Briefcase01Icon, title: t('classification.tabs.accountabilityAreas'), path: '/user/classification/accountability', keywords: ['accountability'] },
    )

    tabs.push(...filterPlanningTabs([
      { icon: CalendarIcon, title: t('planning.tabs.pastRequests'), path: '/user/planning/past-requests', keywords: ['past requests'] },
      { icon: CalendarIcon, title: t('planning.tabs.attendance'), path: '/user/planning/attendance', keywords: ['attendance'] },
      { icon: CalendarIcon, title: t('planning.tabs.leaveManagement'), path: '/user/planning/leave-management', keywords: ['leave management'] },
      { icon: CalendarIcon, title: t('planning.tabs.workspace'), path: '/user/planning/workspace', keywords: ['workspace'] },
      { icon: CalendarIcon, title: t('planning.tabs.timeTracking'), path: '/user/planning/time-tracking', keywords: ['time tracking'] },
    ]))

    tabs.push(
      { icon: Users02Icon, title: t('payroll.tabs.workingDays'), path: '/user/payroll/working-days', keywords: ['working days'] },
      { icon: Users02Icon, title: t('payroll.tabs.benefits'), path: '/user/payroll/benefits', keywords: ['benefits'] },
    )
    if (role === 'org_admin' || role === 'super_admin') {
      tabs.push(
        { icon: Users02Icon, title: t('payroll.tabs.benefitMutations'), path: '/user/payroll/benefit-mutations', keywords: ['benefit mutations'] },
      )
    }

    tabs.push(
      { icon: File04Icon, title: t('documents.tabs.templates'), path: '/user/admin/document/templates', keywords: ['templates'] },
      { icon: File04Icon, title: t('documents.tabs.hrInfo'), path: '/user/admin/document/hrinfo', keywords: ['hr info'] },
    )
    if (role === 'org_admin' || role === 'super_admin') {
      tabs.push(
        { icon: File04Icon, title: t('documents.tabs.statistics'), path: '/user/admin/document/statistics', keywords: ['statistics'] },
      )
    }

    if (role === 'org_admin' || role === 'super_admin') {
      tabs.push(
        { icon: Grid01Icon, title: t('explore.tabs.paths'), path: '/user/explore/paths', keywords: ['paths'] },
        { icon: Grid01Icon, title: t('explore.tabs.statistics'), path: '/user/explore/statistics', keywords: ['statistics'] },
      )
    }

    if (role === 'org_admin' || role === 'super_admin') {
      tabs.push(
        { icon: HomeIcon, title: t('faq.tabs.manage'), path: '/user/faq/manage', keywords: ['faq manage'] },
      )
    }

    if (role === 'org_admin' || role === 'super_admin') {
      tabs.push(
        { icon: Settings01Icon, title: t('settings.tabs.integration'), path: '/user/settings/integration', keywords: ['settings integration'] },
        { icon: Settings01Icon, title: t('settings.tabs.access'), path: '/user/settings/access', keywords: ['settings access'] },
        { icon: Settings01Icon, title: t('settings.tabs.payments'), path: '/user/settings/payments', keywords: ['settings payments'] },
        { icon: Settings01Icon, title: t('settings.tabs.modules'), path: '/user/settings/modules', keywords: ['settings modules'] },
      )
      if (role === 'super_admin') {
        tabs.push(
          { icon: Settings01Icon, title: t('settings.tabs.organizations'), path: '/user/settings/organizations', keywords: ['settings organizations'] },
          { icon: Settings01Icon, title: t('settings.tabs.plans'), path: '/user/settings/plans', keywords: ['settings plans'] },
        )
      }
    }
  }

  return tabs
}

const getTypeConfig = (t: TFunction): Record<string, { label: string; icon: React.ElementType; color: string }> => ({
  page: { label: t('globalSearch.types.pages'), icon: HomeIcon, color: 'primary.100' },
  employee: { label: t('globalSearch.types.employees'), icon: DataflowIcon4, color: 'blue.100' },
  candidate: { label: t('globalSearch.types.candidates'), icon: UserPlus01Icon, color: 'pink.100' },
  document: { label: t('globalSearch.types.documents'), icon: File04Icon, color: 'warning.100' },
  content: { label: t('globalSearch.types.content'), icon: Monitor02Icon, color: 'aqua.100' },
  job_profile: { label: t('globalSearch.types.jobProfiles'), icon: Briefcase01Icon, color: 'success.100' },
})

export function GlobalSearch({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [apiResults, setApiResults] = useState<SearchResult[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { role, isAdmin } = useRole()
  const TYPE_CONFIG = useMemo(() => getTypeConfig(t), [t])
  
  const { data: myPermissions } = useQuery(myPermissionsQueryOptions)
  const planningTabs = myPermissions?.planning || []
  const supportTabs = myPermissions?.support || []

  const PAGES = useMemo(() => {
    const pages = getPagesByRole(role, isAdmin, planningTabs, supportTabs, t)
    const tabs = getTabsByRole(role, isAdmin, planningTabs, myPermissions, t)
    const seen = new Set(pages.map((p) => p.path))
    return [...pages, ...tabs.filter((tab) => !seen.has(tab.path))]
  }, [role, isAdmin, planningTabs, supportTabs, myPermissions, t])

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setSelectedIndex(0)
      setApiResults([])
      setIsLoading(false)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [isOpen])

  // Filter pages by query (client-side)
  const pageResults = useMemo<SearchResult[]>(() => {
    if (!query.trim()) return []
    const q = query.toLowerCase()
    return PAGES
      .filter((p) => p.title.toLowerCase().includes(q) || p.keywords.some((k) => k.includes(q)))
      .map((p) => ({
        id: `page-${p.path}`,
        type: 'page' as const,
        title: p.title,
        href: p.path,
        icon: p.icon,
      }))
  }, [PAGES, query])

  // Debounced API search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!query.trim() || query.length < 2) {
      setApiResults([])
      setIsLoading(false)
      return
    }

    // Set loading immediately
    setIsLoading(true)
    
    debounceRef.current = setTimeout(async () => {
      const searches: Promise<SearchResult[]>[] = []

      // Employees — all roles can see employees
      searches.push(
        searchEmployees(query).then((data: SearchEmployeeResult[]) =>
          data.map((e) => ({
            id: `emp-${e.id}`,
            type: 'employee' as const,
            title: `${e.first_name} ${e.last_name}`.trim() || e.work_email,
            subtitle: (typeof e.current_position === 'object' && e.current_position?.title) || e.work_email,
            href: `/user/workforce/people/${e.id}`,
            avatar: e.profile_picture,
          }))
        ).catch(() => [])
      )

      // Candidates — admin roles only
      if (isAdmin && ['super_admin', 'org_admin', 'country_admin'].includes(role || '')) {
        searches.push(
          searchCandidates(query).then((data: SearchCandidateResult[]) =>
            data.map((c) => ({
              id: `cand-${c.id}`,
              type: 'candidate' as const,
              title: `${c.first_name} ${c.last_name}`.trim() || c.email,
              subtitle: c.position_name || c.email,
              href: `/user/recruitment/candidates?candidate=${c.id}`,
            }))
          ).catch(() => [])
        )
      }

      // Documents — admin roles
      if (isAdmin) {
        searches.push(
          searchDocuments(query).then((data: Array<SearchDocumentResult & { title?: string }>) =>
            data.map((d) => ({
              id: `doc-${d.id}`,
              type: 'document' as const,
              title: d.name || d.title || t('common.labels.untitled'),
              subtitle: d.template_name,
              href: `/user/admin/document/templates`,
            }))
          ).catch(() => [])
        )
      }

      // Content — org_admin
      if (isAdmin && (role === 'org_admin' || role === 'super_admin')) {
        searches.push(
          searchContent(query).then((data: SearchContentResult[]) =>
            data.map((c) => ({
              id: `content-${c.id}`,
              type: 'content' as const,
              title: c.title || t('common.labels.untitled'),
              subtitle: c.topic,
              href: `/user/content/${c.id}/details`,
            }))
          ).catch(() => [])
        )
      }

      // Job Profiles — admin roles
      if (isAdmin && ['super_admin', 'org_admin', 'country_admin'].includes(role || '')) {
        searches.push(
          searchJobProfiles(query).then((data: SearchJobProfileResult[]) =>
            data.map((j) => ({
              id: `jp-${j.id}`,
              type: 'job_profile' as const,
              title: j.title || t('common.labels.untitled'),
              href: `/user/classification/jobprofile`,
            }))
          ).catch(() => [])
        )
      }

      // Execute all searches in parallel and update results as they come in
      Promise.all(searches)
        .then((results) => {
          setApiResults(results.flat())
          setIsLoading(false)
        })
        .catch(() => {
          setApiResults([])
          setIsLoading(false)
        })
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, role, isAdmin, t])

  // Combine all results
  const allResults = useMemo(() => [...pageResults, ...apiResults], [pageResults, apiResults])

  // Group by type
  const grouped = useMemo(() => {
    const groups: Record<string, SearchResult[]> = {}
    for (const r of allResults) {
      if (!groups[r.type]) groups[r.type] = []
      groups[r.type].push(r)
    }
    return groups
  }, [allResults])

  const handleSelect = useCallback(
    (result: SearchResult) => {
      navigate(result.href)
      onClose()
    },
    [navigate, onClose]
  )

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((i) => Math.min(i + 1, allResults.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === 'Enter' && allResults.length > 0) {
        e.preventDefault()
        handleSelect(allResults[selectedIndex])
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, allResults, selectedIndex, handleSelect])

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  // Scroll selected item into view
  useEffect(() => {
    if (!resultsRef.current) return
    const selected = resultsRef.current.querySelector('[data-selected="true"]')
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  let flatIndex = -1

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" motionPreset="scale">
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(8px)" />
      <ModalContent
        maxW="720px"
        mt="12vh"
        mx="auto"
        bg="white"
        borderRadius="20px"
        boxShadow="0 32px 96px rgba(93, 94, 203, 0.15), 0 12px 32px rgba(0, 0, 0, 0.1)"
        overflow="hidden"
      >
        <ModalBody p={0}>
          {/* Search input */}
          <Flex align="center" gap="12px" px="20px" py="14px" borderBottom="1px solid" borderColor="solid.50">
            <Flex align="center" flex={1} position="relative">
              <Icon
                width="20px"
                height="20px"
                fill="none"
                color={query ? 'solid.500' : 'solid.300'}
                position="absolute"
                left="0"
                pointerEvents="none"
                transition="color 0.2s"
              >
                <SearchLgIcon />
              </Icon>
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('common.labels.searchPlaceholder', 'Search pages, people, documents...')}
                variant="unstyled"
                h="40px"
                pl="32px"
                pr="8px"
                fontSize="15px"
                fontWeight="450"
                color="solid.900"
                _placeholder={{ color: 'solid.400', fontWeight: '400' }}
                _focus={{ outline: 'none' }}
              />
            </Flex>
            {isLoading && (
              <Spinner
                size="sm"
                color="primary.100"
                thickness="2px"
                speed="0.65s"
                flexShrink={0}
              />
            )}
            <Kbd
              bg="solid.25"
              border="1px solid"
              borderColor="solid.100"
              borderRadius="6px"
              px="8px"
              py="3px"
              fontSize="11px"
              fontWeight="600"
              color="solid.500"
              flexShrink={0}
              letterSpacing="0.02em"
            >
              ESC
            </Kbd>
          </Flex>

          {/* Results */}
          <Box
            ref={resultsRef}
            maxH="480px"
            overflowY="auto"
            py="6px"
            css={{
              scrollBehavior: 'smooth',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
                marginTop: '8px',
                marginBottom: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0,0,0,0.1)',
                borderRadius: '10px',
                transition: 'background 0.2s ease',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(0,0,0,0.15)',
              },
            }}
          >
            {/* Empty state — quick navigation */}
            {!query.trim() && (
              <VStack align="stretch" spacing={0} px="8px" py="4px">
                <Text
                  fontSize="11px"
                  fontWeight="600"
                  color="solid.400"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                  px="10px"
                  py="6px"
                >
                  {t('globalSearch.quickNavigation')}
                </Text>
                {PAGES.map((page) => {
                  const PageIcon = page.icon
                  return (
                    <Flex
                      key={page.path}
                      as="button"
                      align="center"
                      gap="10px"
                      px="12px"
                      py="10px"
                      borderRadius="12px"
                      cursor="pointer"
                      transition="all 0.15s cubic-bezier(0.22, 1, 0.36, 1)"
                      _hover={{ bg: 'primary.0', transform: 'translateX(2px)' }}
                      onClick={() => {
                        navigate(page.path)
                        onClose()
                      }}
                    >
                      <Flex
                        align="center"
                        justify="center"
                        w="28px"
                        h="28px"
                        borderRadius="8px"
                        bg="solid.25"
                      >
                        <Icon width="16px" height="16px" fill="none" color="solid.500">
                          <PageIcon />
                        </Icon>
                      </Flex>
                      <Text fontSize="13px" fontWeight="500" color="solid.700" flex={1} textAlign="left">
                        {page.title}
                      </Text>
                      <Text fontSize="11px" color="solid.300">↵</Text>
                    </Flex>
                  )
                })}
              </VStack>
            )}

            {/* No results */}
            {query.trim() && !isLoading && allResults.length === 0 && (
              <Flex direction="column" align="center" py="40px" gap="8px">
                <Icon width="32px" height="32px" fill="none" color="solid.200">
                  <SearchLgIcon />
                </Icon>
                <Text fontSize="14px" color="solid.400" fontWeight="500">
                  {t('globalSearch.noResults', { query })}
                </Text>
                <Text fontSize="12px" color="solid.300">
                  {t('globalSearch.tryDifferentSearch')}
                </Text>
              </Flex>
            )}

            {/* Grouped results */}
            {Object.entries(grouped).map(([type, items]) => {
              const config = TYPE_CONFIG[type]
              if (!config) return null
              return (
                <Box key={type} px="8px" py="2px">
                  <Text
                    fontSize="11px"
                    fontWeight="600"
                    color="solid.400"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    px="10px"
                    py="6px"
                  >
                    {config.label}
                  </Text>
                  <VStack align="stretch" spacing={0}>
                    {items.map((result) => {
                      flatIndex++
                      const isSelected = flatIndex === selectedIndex
                      const currentIdx = flatIndex
                      const TypeIcon = result.icon || config.icon

                      return (
                        <Flex
                          key={result.id}
                          as="button"
                          align="center"
                          gap="10px"
                          px="12px"
                          py="10px"
                          borderRadius="12px"
                          bg={isSelected ? 'primary.0' : 'transparent'}
                          cursor="pointer"
                          transition="all 0.15s cubic-bezier(0.22, 1, 0.36, 1)"
                          _hover={{ bg: 'primary.0', transform: 'translateX(2px)' }}
                          onClick={() => handleSelect(result)}
                          onMouseEnter={() => setSelectedIndex(currentIdx)}
                          data-selected={isSelected}
                        >
                          {result.avatar ? (
                            <FastAvatar
                              src={result.avatar}
                              name={result.title}
                              boxSize="28px"
                              flexShrink={0}
                            />
                          ) : (
                            <Flex
                              align="center"
                              justify="center"
                              w="28px"
                              h="28px"
                              borderRadius="8px"
                              bg={type === 'page' ? 'solid.25' : `${config.color}15`}
                              flexShrink={0}
                            >
                              <Icon width="16px" height="16px" fill="none" color={config.color}>
                                <TypeIcon />
                              </Icon>
                            </Flex>
                          )}
                          <Box flex={1} minW={0} textAlign="left">
                            <Text
                              fontSize="13px"
                              fontWeight="500"
                              color="solid.800"
                              noOfLines={1}
                            >
                              {result.title}
                            </Text>
                            {result.subtitle && (
                              <Text fontSize="11px" color="solid.400" noOfLines={1}>
                                {result.subtitle}
                              </Text>
                            )}
                          </Box>
                          {isSelected && (
                            <Text fontSize="11px" color="solid.300" flexShrink={0}>
                              ↵
                            </Text>
                          )}
                        </Flex>
                      )
                    })}
                  </VStack>
                </Box>
              )
            })}
          </Box>

          {/* Footer */}
          {(allResults.length > 0 || !query.trim()) && (
            <Flex
              px="16px"
              py="10px"
              borderTop="1px solid"
              borderColor="solid.50"
              justify="space-between"
              align="center"
            >
              <Flex gap="16px" fontSize="11px" color="solid.400">
                <Flex align="center" gap="4px">
                  <Kbd size="sm" bg="solid.25" border="1px solid" borderColor="solid.100" borderRadius="4px" px="4px" py="1px" fontSize="10px" color="solid.400">↑↓</Kbd>
                  <Text>{t('globalSearch.footer.navigate')}</Text>
                </Flex>
                <Flex align="center" gap="4px">
                  <Kbd size="sm" bg="solid.25" border="1px solid" borderColor="solid.100" borderRadius="4px" px="4px" py="1px" fontSize="10px" color="solid.400">↵</Kbd>
                  <Text>{t('globalSearch.footer.open')}</Text>
                </Flex>
                <Flex align="center" gap="4px">
                  <Kbd size="sm" bg="solid.25" border="1px solid" borderColor="solid.100" borderRadius="4px" px="4px" py="1px" fontSize="10px" color="solid.400">esc</Kbd>
                  <Text>{t('globalSearch.footer.close')}</Text>
                </Flex>
              </Flex>
              {allResults.length > 0 && (
                <Text fontSize="11px" color="solid.400">
                  {allResults.length} {allResults.length !== 1 ? t('globalSearch.resultsCount') : t('globalSearch.resultCount')}
                </Text>
              )}
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default GlobalSearch
